import { useEffect, useRef, useState } from "react";
import { BsX } from "react-icons/bs";
import styled from "styled-components";
import { StudentOptionViewModel } from "../../../../domain/viewmodels/report/StudentOptionViewModel";
import EcommerceManagementServices from "../../../../services/EcommerceManagementServices";
import { getInstitutionId } from "../../../../util/UserUtil";

const ecommerceService = new EcommerceManagementServices();

interface StudentAutocompleteProps {
    value: string;
    onSelect: (studentId: string, studentName: string) => void;
    onClear: () => void;
}

export function StudentAutocomplete({ value, onSelect, onClear }: StudentAutocompleteProps) {
    const [inputValue, setInputValue] = useState(value);
    const [suggestions, setSuggestions] = useState<StudentOptionViewModel[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
                setIsFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChange = (term: string) => {
        setInputValue(term);

        if (term.length === 0) {
            onClear();
            setSuggestions([]);
            setShowDropdown(false);
            setNoResults(false);
            return;
        }

        if (term.length < 3) {
            setSuggestions([]);
            setShowDropdown(false);
            setNoResults(false);
            return;
        }

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(async () => {
            const institutionId = getInstitutionId();
            if (!institutionId) return;

            const response = await ecommerceService.searchStudents(institutionId, term);

            if (response && response.success && response.result) {
                const top3 = response.result.slice(0, 3);
                setSuggestions(top3);
                setNoResults(top3.length === 0);
                setShowDropdown(true);
            } else {
                setSuggestions([]);
                setNoResults(true);
                setShowDropdown(true);
            }
        }, 300);
    };

    const handleSelect = (student: StudentOptionViewModel) => {
        setInputValue(student.name);
        setSuggestions([]);
        setShowDropdown(false);
        setNoResults(false);
        onSelect(student.id, student.name);
    };

    const handleClear = () => {
        setInputValue("");
        setSuggestions([]);
        setShowDropdown(false);
        setNoResults(false);
        onClear();
    };

    return (
        <AutocompleteWrapper ref={wrapperRef} isFocused={isFocused}>
            <div className="input-control">
                <input
                    type="text"
                    className="select-input"
                    placeholder="Escreva o nome do aluno (mínimo 3 caracteres)"
                    value={inputValue}
                    onChange={e => handleChange(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    autoComplete="off"
                />
                {inputValue && (
                    <span className="clear-btn" onMouseDown={handleClear}>
                        <BsX />
                    </span>
                )}
            </div>
            {showDropdown && (
                <div className="suggestions-dropdown">
                    {noResults ? (
                        <div className="suggestion-item no-results">Nenhuma correspondência</div>
                    ) : (
                        suggestions.map(s => (
                            <div
                                key={s.id}
                                className="suggestion-item"
                                onMouseDown={() => handleSelect(s)}
                            >
                                {s.name}
                            </div>
                        ))
                    )}
                </div>
            )}
        </AutocompleteWrapper>
    );
}

const AutocompleteWrapper = styled.div<{ isFocused: boolean }>`
    position: relative;

    .input-control {
        display: flex;
        align-items: center;
        border: 1px solid ${p => p.isFocused ? '#6468C8' : '#b3b5df'};
        border-radius: 8px;
        background: #fff;
        min-height: 38px;
        box-shadow: ${p => p.isFocused ? '0px 0px 4px #6468c8' : 'none'};
        transition: border-color 0.15s, box-shadow 0.15s;
    }

    input.select-input {
        flex: 1;
        border: none;
        outline: none;
        background: transparent;
        padding: 0.375rem 0.75rem;
        font-family: 'Lato', sans-serif;
        font-weight: 400;
        font-style: normal;
        font-size: 0.85rem;
        letter-spacing: 0px;
        color: #514F4E;
        :placeholder {
            font-family: 'Lato', sans-serif !important;
            font-weight: 400 !important;
            font-style: normal !important;
            font-size: 0.85rem !important;
            letter-spacing: 0px;
            color: #808080 !important;
        }
    }

    .clear-btn {
        display: flex;
        align-items: center;
        padding: 0 4px;
        margin-right: 4px;
        cursor: pointer;
        color: #ccc;
        font-size: 1.5rem;

        svg {
            stroke-width: 0.5px;
        }

        &:hover {
            color: #999;
        }
    }

    .separator {
        width: 1px;
        height: 20px;
        background-color: #ccc;
        margin: 0 4px;
    }

    .indicator {
        display: flex;
        align-items: center;
        padding: 0 8px 0 4px;

        svg {
            fill: #ccc;
        }
    }

    .suggestions-dropdown {
        position: absolute;
        z-index: 1050;
        width: 100%;
        background: #fff;
        border: 1px solid #b3b5df;
        border-radius: 0 0 8px 8px;
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        max-height: 15vh;
        overflow-y: auto;
        margin-top: -1px;
    }

    .suggestion-item {
        padding: 0.5rem 0.75rem;
        font-size: 0.85rem;
        font-weight: 400;
        font-family: 'Lato', sans-serif;
        color: #514F4E;
        cursor: pointer;

        &:hover {
            background: #8487e0;
            color: #FFF;
        }

        &.no-results {
            color: #7A7A7A;
            cursor: default;

            &:hover {
                background: transparent;
                color: #7A7A7A;
            }
        }
    }
`;
